

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import type { Post, User, Comment as CommentType } from '../types';
import HeartIcon from './icons/HeartIcon';
import BookmarkIcon from './icons/BookmarkIcon';
import HeartFilledIcon from './icons/HeartFilledIcon';
import PaperAirplaneIcon from './icons/PaperAirplaneIcon';
import ThreeDotsIcon from './icons/ThreeDotsIcon';
import GearIcon from './icons/GearIcon';
import MapPinIcon from './icons/MapPinIcon';
import ExclamationIcon from './icons/ExclamationIcon';
import MediaTextOverlay from './MediaTextOverlay';
import OverlayLayer from './OverlayLayer';
import ReactionPicker from './ReactionPicker';
import PopupMenu from './PopupMenu';
import UserIcon from './icons/UserIcon';
import TrashIcon from './icons/TrashIcon';
import SpeakerWaveIcon from './icons/SpeakerWaveIcon';
import { getStaticMapTileUrl } from '../utils/staticMap';

interface PostCardProps {
  post: Post;
  currentUser: User | null;
  allUsers: User[];
  onToggleLike: (postId: string) => void;
  onReactToPost?: (postId: string, emoji: string) => void;
  onAddComment: (postId: string, text: string, parentId?: string | null) => void | Promise<void>;
  onOpenComments: (post: Post) => void;
  onSharePost: (postId: string, recipientId: string) => void;
  onOpenSharePage: (post: Post) => void;
  onPromptLogin: () => void;
  onViewProfile: (user: User) => void;
  onToggleSave: (postId: string) => void;
  onDeletePost: (postId: string) => void;
  onOpenLocation?: (post: Post) => void;
  onBanUser?: (userId: string) => void;
  onReportContent?: (type: 'post', targetId: string) => void;
}

// Card shape follows the media, clamped so very tall/wide shots stay reasonable.
const MIN_RATIO = 0.75;
const MAX_RATIO = 1.4;
const DEFAULT_RATIO = 0.8;
const ratioCache = new Map<string, number>();
const clampRatio = (w: number, h: number) => (w > 0 && h > 0 ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, w / h)) : DEFAULT_RATIO);

// One sound setting shared by every post video: tap the speaker on any post and the rest follow. Videos start muted
// (browsers only allow sound after a tap) and stay on whatever the viewer last chose.
let postVideosMuted = true;
const muteListeners = new Set<() => void>();
const setPostVideosMuted = (value: boolean) => {
  postVideosMuted = value;
  muteListeners.forEach((fn) => fn());
};
const usePostVideosMuted = () => {
  const [muted, setMuted] = useState(postVideosMuted);
  useEffect(() => {
    const sync = () => setMuted(postVideosMuted);
    muteListeners.add(sync);
    sync();
    return () => { muteListeners.delete(sync); };
  }, []);
  return muted;
};

const CarouselVideo: React.FC<{ src: string; active: boolean; muted: boolean }> = ({ src, active, muted }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) v.play().catch(() => {});
    else v.pause();
  }, [active]);
  return (
    <video
      ref={ref}
      src={src.includes('#') ? src : `${src}#t=0.001`}
      className="h-full w-full bg-black object-cover"
      preload="metadata"
      muted={muted}
      loop
      playsInline
      disablePictureInPicture
      onClick={(e) => { const v = e.currentTarget; if (v.paused) v.play().catch(() => {}); else v.pause(); }}
    />
  );
};

const PostCard: React.FC<PostCardProps> = React.memo(({ post, currentUser, allUsers, onToggleLike, onReactToPost, onAddComment, onOpenComments, onSharePost, onOpenSharePage, onPromptLogin, onViewProfile, onToggleSave, onDeletePost, onOpenLocation, onBanUser, onReportContent }) => {
  const { id, user, imageUrl, caption, likedBy, timestamp, sport, tricks = [], savedBy = [] } = post;
  const [commentsCount, setCommentsCount] = useState(0);
  const [previewComments, setPreviewComments] = useState<CommentType[]>([]);
  const [showLikeHeart, setShowLikeHeart] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const touchStartXRef = useRef<number | null>(null);

  const carouselItems = post.mediaItems && post.mediaItems.length > 1 ? post.mediaItems : null;
  const [slideIndex, setSlideIndex] = useState(0);
  // A carousel handles its own slides, so the single-video machinery stays off.
  const mediaType = carouselItems ? 'image' : (post.mediaType || (post.videoUrlCompressed ? 'video' : 'image'));
  const mediaUrl = mediaType === 'video' ? (post.videoUrlCompressed || imageUrl) : imageUrl;
  const ratioKey = carouselItems ? carouselItems[0].url : mediaUrl;
  const [mediaRatio, setMediaRatio] = useState<number>(() => ratioCache.get(ratioKey) ?? DEFAULT_RATIO);
  const reportSize = (w: number, h: number) => {
    const r = clampRatio(w, h);
    ratioCache.set(ratioKey, r);
    setMediaRatio(r);
  };

  const progressRafRef = useRef<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const progressTrackRef = useRef<HTMLDivElement | null>(null);
  const lastTapRef = useRef<number>(0);
  const isScrubbingRef = useRef(false);
  const wasPlayingBeforeScrubRef = useRef(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const isVideoMuted = usePostVideosMuted();
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  const isLiked = currentUser ? likedBy.includes(currentUser.id) : false;
  const isSaved = currentUser ? savedBy.includes(currentUser.id) : false;
  const canDelete = currentUser?.id === user.id;
  const isModerator = currentUser?.username?.toLowerCase() === 'ridaz';
  const [isModMenuOpen, setIsModMenuOpen] = useState(false);

  const handleModDeleteClick = () => {
    setIsModMenuOpen(false);
    const confirmed = window.confirm('Take down this post? This cannot be undone.');
    if (confirmed) onDeletePost(id);
  };

  const handleModBanClick = () => {
    setIsModMenuOpen(false);
    onBanUser?.(user.id);
  };
  const formatTimeAgo = (ts: string | number | undefined) => {
    if (!ts) return '';
    const now = Date.now();
    const t = typeof ts === 'number' ? ts : new Date(ts).getTime();
    if (!Number.isFinite(t)) return '';
    const diff = Math.max(0, now - t);
    const sec = Math.floor(diff / 1000);
    if (sec < 45) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    const week = Math.floor(day / 7);
    if (week < 5) return `${week}w ago`;
    const month = Math.floor(day / 30);
    if (month < 12) return `${month}mo ago`;
    const year = Math.floor(day / 365);
    return `${year}y ago`;
  };

  useEffect(() => {
    const toMillis = (value: unknown): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') return new Date(value).getTime() || 0;
      if (value && typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate().getTime();
      }
      return 0;
    };

    const commentsRef = collection(db, 'posts', post.id, 'comments');
    const unsubscribe = onSnapshot(commentsRef, (snapshot) => {
        setCommentsCount(snapshot.size);

        // Pinned below the caption: the single most popular (most-liked) top-level comment; when none has any
        // likes yet, the most recent one.
        const topLevel = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() } as CommentType))
          .filter((c) => !c.parentId);
        const ranked = [...topLevel].sort((a, b) => {
          const diff = (b.likedBy?.length || 0) - (a.likedBy?.length || 0);
          return diff !== 0 ? diff : toMillis(b.timestamp) - toMillis(a.timestamp);
        });
        setPreviewComments(ranked.slice(0, 1));
    });
    return () => unsubscribe();
  }, [post.id]);

  const handleLikeClick = () => {
    if (!currentUser) {
      onPromptLogin();
    } else {
      onToggleLike(id);
    }
  };
  
  const handleDoubleClickLike = () => {
    if (!currentUser) {
      onPromptLogin();
      return;
    }
    if (!isLiked) {
      onToggleLike(id);
    }
    setShowLikeHeart(true);
    setTimeout(() => {
      setShowLikeHeart(false);
    }, 600);
  };

  const handleCommentClick = () => {
    onOpenComments(post);
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) { onPromptLogin(); return; }
    setIsSettingsOpen(false);
    setShowShareMenu(prev => !prev);
  };

  const handleShareInApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareMenu(false);
    onOpenSharePage(post);
  };

  const handleShareOutside = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareMenu(false);
    try {
      await navigator.share({
        title: `${user.username}'s ${sport.name} post on RIDAZ`,
        text: caption,
        url: window.location.href,
      });
    } catch (_) {
      // User cancelled or share not available
    }
  };

  const mentionLookup = useMemo(() => {
    const map: Record<string, User> = {};
    allUsers.forEach(u => { map[u.username.toLowerCase()] = u; });
    return map;
  }, [allUsers]);

  const renderWithMentions = (text: string) => {
    return text.split(/(@[A-Za-z0-9_]+)/g).map((part, idx) => {
      if (!part.startsWith('@')) return <span key={idx}>{part}</span>;
      const username = part.slice(1).toLowerCase();
      const target = mentionLookup[username];
      if (!target) return <span key={idx} className="text-[13px] text-lime-300">{part}</span>;
      return (
        <button
          key={idx}
          className="text-[13px] text-lime-300 hover:underline"
          onClick={(e) => { e.stopPropagation(); onViewProfile(target); }}
        >
          {part}
        </button>
      );
    });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setIsSettingsOpen(false);
        setShowShareMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Auto-play video when in view
  useEffect(() => {
    if (!videoRef.current || mediaType !== 'video') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoRef.current?.play().catch((err) => console.log('Autoplay prevented:', err));
          } else {
            videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [mediaType]);

  useEffect(() => {
    const handleFsChange = () => {
      const fsEl = (document as any).fullscreenElement || (document as any).webkitFullscreenElement;
      const isFs = fsEl === videoRef.current;
      setIsVideoFullscreen(Boolean(isFs));
      if (!isFs) {
        setVideoProgress(0);
      }
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange as any);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange as any);
    };
  }, []);

  // Clean up RAF on unmount
  useEffect(() => {
    return () => {
      if (progressRafRef.current) {
        cancelAnimationFrame(progressRafRef.current);
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);


  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewProfile(user);
  };

  // Progress tracking helpers
  const computeProgress = () => {
    const video = videoRef.current;
    if (!video || !video.duration || Number.isNaN(video.duration)) return 0;
    return Math.min(Math.max(video.currentTime / video.duration, 0), 1);
  };

  const applyProgress = (val: number) => {
    const clamped = Math.min(Math.max(val, 0), 1);
    // Only re-render React when the whole-percent value changes; the bar itself is moved directly via its ref every
    // frame. Re-rendering the whole card 60x a second made playback stutter.
    setVideoProgress((prev) => (Math.round(prev * 100) === Math.round(clamped * 100) ? prev : clamped));
    if (progressBarRef.current) {
      progressBarRef.current.style.transform = `scaleX(${clamped})`;
    }
  };

  const stopProgressLoop = () => {
    if (progressRafRef.current) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = null;
    }
  };

  const tickProgress = () => {
    applyProgress(computeProgress());
    const video = videoRef.current;
    if (!video || video.paused || video.ended) {
      progressRafRef.current = null;
      return;
    }
    progressRafRef.current = requestAnimationFrame(tickProgress);
  };

  const startProgressLoop = () => {
    if (progressRafRef.current) return;
    progressRafRef.current = requestAnimationFrame(tickProgress);
  };

  const handleVideoPlay = () => {
    applyProgress(0);
    // If play fires mid-stream, re-sync immediately
    const pct = computeProgress();
    if (pct > 0) applyProgress(pct);
    startProgressLoop();
  };

  const handleVideoPause = () => {
    stopProgressLoop();
    applyProgress(computeProgress());
  };

  // Tap anywhere on the video to pause, tap again to play. The tap waits a beat so a double-tap (like) doesn't also
  // pause/resume the video.
  // Bottom-left badge: two pause bars while paused by the user, flips to a play triangle when they resume, then fades.
  const [playBadge, setPlayBadge] = useState<'paused' | 'playing' | null>(null);
  const playBadgeTimerRef = useRef<number | null>(null);
  const videoTapTimerRef = useRef<number | null>(null);
  const handleVideoTap = () => {
    if (videoTapTimerRef.current !== null) {
      window.clearTimeout(videoTapTimerRef.current);
      videoTapTimerRef.current = null;
      return;
    }
    videoTapTimerRef.current = window.setTimeout(() => {
      videoTapTimerRef.current = null;
      const video = videoRef.current;
      if (!video) return;
      if (playBadgeTimerRef.current !== null) { window.clearTimeout(playBadgeTimerRef.current); playBadgeTimerRef.current = null; }
      if (video.paused || video.ended) {
        setPlayBadge('playing');
        playBadgeTimerRef.current = window.setTimeout(() => { setPlayBadge(null); playBadgeTimerRef.current = null; }, 900);
        video.play().catch(() => undefined);
      } else {
        setPlayBadge('paused');
        video.pause();
      }
    }, 260);
  };

  const handleLoadedMetadata = () => {
    applyProgress(0);
  };

  const seekToClientX = (clientX: number) => {
    const video = videoRef.current;
    const track = progressTrackRef.current;
    if (!video || !track || !video.duration || Number.isNaN(video.duration)) return;
    const rect = track.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
    applyProgress(ratio);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!isScrubbingRef.current) return;
    seekToClientX(event.clientX);
  };

  const handlePointerUp = () => {
    if (!isScrubbingRef.current) return;
    isScrubbingRef.current = false;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    const video = videoRef.current;
    if (video && wasPlayingBeforeScrubRef.current) {
      video.play().catch(() => undefined);
      startProgressLoop();
    }
  };

  const handleProgressPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    isScrubbingRef.current = true;
    wasPlayingBeforeScrubRef.current = !video.paused && !video.ended;
    stopProgressLoop();
    video.pause();
    seekToClientX(event.clientX);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp, { once: true });
  };

  const handleDeleteClick = () => {
    if (!canDelete) return;
    const confirmed = window.confirm('Delete this post? This cannot be undone.');
    if (confirmed) {
      onDeletePost(id);
      setIsSettingsOpen(false);
    }
  };

  return (
    <div className="relative">
    <div ref={cardRef} className="ridaz-post postcard-container bg-transparent rounded-lg overflow-visible mb-4 max-w-2xl mx-auto w-full relative postcard-wrapper flex flex-col text-white shadow-[0_18px_44px_rgba(0,0,0,0.28)]">
      <div
        className="paper-on-media relative postcard-media-shell mt-0 overflow-hidden rounded-2xl bg-black"
        style={{ aspectRatio: String(mediaRatio) }}
        onDoubleClick={handleDoubleClickLike}
        onTouchStart={(e) => { touchStartXRef.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const startX = touchStartXRef.current;
          touchStartXRef.current = null;
          const dx = startX !== null ? e.changedTouches[0].clientX - startX : 0;
          // Double-tap to like (only if minimal movement)
          if (Math.abs(dx) < 20) {
            const now = Date.now();
            if (now - lastTapRef.current < 300) handleDoubleClickLike();
            lastTapRef.current = now;
          }
        }}
      >
        {carouselItems ? (
          <>
            <div
              className="postcard-media flex h-full w-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onScroll={(e) => {
                const el = e.currentTarget;
                const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
                if (idx !== slideIndex) setSlideIndex(idx);
              }}
            >
              {carouselItems.map((item, i) => (
                <div key={`${item.url}-${i}`} className="relative h-full w-full shrink-0 snap-center">
                  {item.type === 'video' ? (
                    <CarouselVideo src={item.url} active={i === slideIndex} muted={isVideoMuted} />
                  ) : (
                    <img src={item.url} alt={`Post content ${i + 1}`} className="h-full w-full object-cover" draggable={false} onLoad={i === 0 ? (e) => reportSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight) : undefined} onContextMenu={(e) => e.preventDefault()} />
                  )}
                  <OverlayLayer overlays={post.overlays?.[i]} fit="cover" />
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute right-3 bottom-3 z-[2] rounded-full bg-black/65 px-3 py-1.5 text-sm font-black text-white backdrop-blur-sm">{slideIndex + 1}/{carouselItems.length}</div>
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[2] flex justify-center">
              {/* Big page dots on a dark pill, so it is obvious the post has more than one photo or video. */}
              <div className="flex items-center gap-2 rounded-full bg-black/55 px-3 py-2 backdrop-blur-sm">
                {carouselItems.map((_, i) => (
                  <span key={i} className={`h-3 rounded-full transition-all ${i === slideIndex ? 'w-7 bg-white' : 'w-3 bg-white/55'}`} />
                ))}
              </div>
            </div>
          </>
        ) : mediaType === 'video' ? (
          <video
            ref={videoRef}
            // #t=0.001 makes iOS paint the first frame instead of black while
            // the video buffers; preload=metadata stops every video in a long
            // viewer list from downloading at once (the IntersectionObserver
            // above starts playback when a card scrolls into view).
            src={mediaUrl.includes('#') ? mediaUrl : `${mediaUrl}#t=0.001`}
            preload="metadata"
            className={`bg-black postcard-media ${isVideoFullscreen ? 'w-screen h-screen max-h-none object-contain' : 'w-full h-full object-cover'}`}
            controls={false}
            controlsList="nofullscreen noremoteplayback noplaybackrate nodownload"
            loop={false}
            muted={isVideoMuted}
            playsInline
            onClick={handleVideoTap}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            onLoadedMetadata={(e) => { handleLoadedMetadata(); reportSize(e.currentTarget.videoWidth, e.currentTarget.videoHeight); }}
            onTimeUpdate={() => applyProgress(computeProgress())}
            onEnded={() => {
              const video = videoRef.current;
              if (video) {
                video.currentTime = 0;
                applyProgress(1);
                setTimeout(() => {
                  applyProgress(0);
                  video.play().catch(() => {});
                  handleVideoPlay();
                }, 180);
              }
            }}
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <img src={mediaUrl} alt="Post content" className="postcard-media w-full h-full soft-zoom object-cover" draggable={false} onLoad={(e) => reportSize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} onContextMenu={(e) => e.preventDefault()} />
        )}
        <div className="pointer-events-none absolute inset-x-3 top-3 z-[2] flex items-start justify-between gap-2">
          <div className="paper-on-media ridaz-post-author pointer-events-auto flex items-center gap-2.5 rounded-xl border border-white/10 bg-black/60 py-2 pl-2 pr-4 backdrop-blur-sm shadow-lg">
            <button onClick={handleProfileClick} className="shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500">
              <img src={user.avatarUrl} alt={user.username} className="h-11 w-11 rounded-xl border-2 border-lime-500 object-cover" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <button onClick={handleProfileClick} className="truncate text-left text-lg font-black leading-none text-white hover:underline focus:outline-none">{'@' + String(user.username).toUpperCase()}</button>
                {user.isOnline && <div className="pulsing-dot-green w-2 h-2 shrink-0"></div>}
              </div>
              <p className="mt-0.5 text-[11px] font-medium leading-none text-white/60">{formatTimeAgo(timestamp)}</p>
            </div>
          </div>
          {post.locationTag && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenLocation?.(post); }}
              className="pointer-events-auto relative h-24 w-36 shrink-0 overflow-hidden rounded-2xl border border-lime-500/50 bg-black shadow-lg transition hover:brightness-110 active:scale-95"
              aria-label={`Open ${post.locationTag.name} on the map`}
            >
              <img
                src={getStaticMapTileUrl(post.locationTag.lat, post.locationTag.lng)}
                alt=""
                className="h-full w-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/10" />
              <MapPinIcon className="absolute left-1/2 top-[42%] h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-lime-400 drop-shadow" />
              <p className="absolute inset-x-0 bottom-0 truncate px-2.5 pb-1.5 text-xs font-extrabold text-lime-100">{post.locationTag.name}</p>
            </button>
          )}
        </div>
        {tricks.length > 0 && (
          <div className="pointer-events-none absolute left-3 top-16 z-[2] flex max-w-[70%] flex-wrap gap-2">
            {tricks.map((trick) => (
              <span
                key={trick}
                className="rounded-full border border-white/15 bg-black/65 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-white backdrop-blur-sm"
              >
                {trick}
              </span>
            ))}
          </div>
        )}
        {isModerator && (
          <div className="absolute right-3 top-20 z-[3]">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIsModMenuOpen((prev) => !prev); }}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-900/50 ring-2 ring-white/20 transition-colors hover:bg-red-500"
              aria-label="Moderator actions"
            >
              <ExclamationIcon className="h-6 w-6" />
            </button>
            {isModMenuOpen && (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-30 w-48 overflow-hidden rounded-lg border border-red-500/40 bg-black shadow-xl">
                <button
                  className="w-full px-3 py-2.5 text-left text-sm font-semibold text-red-400 transition-colors hover:bg-white/10"
                  onClick={(e) => { e.stopPropagation(); handleModDeleteClick(); }}
                >
                  Take down post
                </button>
                <button
                  className="w-full px-3 py-2.5 text-left text-sm font-semibold text-red-400 transition-colors hover:bg-white/10"
                  onClick={(e) => { e.stopPropagation(); handleModBanClick(); }}
                >
                  Ban {'@' + String(user.username).toUpperCase()}
                </button>
              </div>
            )}
          </div>
        )}
        <MediaTextOverlay overlay={post.textOverlay} />
        {!carouselItems && <OverlayLayer overlays={post.overlays?.[0]} fit="cover" />}
        {mediaType === 'video' && (
          <div
            ref={progressTrackRef}
            className="absolute inset-x-0 bottom-0 h-3 cursor-ew-resize overflow-hidden bg-black/70 backdrop-blur-sm touch-none"
            onPointerDown={handleProgressPointerDown}
            role="slider"
            aria-label="Seek post video"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(videoProgress * 100)}
          >
            <div
              ref={progressBarRef}
              className="h-full bg-white origin-left transition-transform duration-150 ease-linear will-change-transform"
              style={{ transform: 'scaleX(0)' }}
            />
          </div>
        )}
        {mediaType === 'video' && !carouselItems && playBadge && (
          <div className="pointer-events-none absolute bottom-6 left-3 z-[3] flex h-10 w-10 items-center justify-center rounded-xl bg-black/55 text-white backdrop-blur-sm" aria-hidden="true">
            {playBadge === 'paused' ? (
              <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current"><rect x="6" y="5" width="4" height="14" rx="1.2" /><rect x="14" y="5" width="4" height="14" rx="1.2" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="ml-0.5 h-6 w-6 fill-current"><path d="M8 5.14v13.72a1 1 0 0 0 1.52.86l10.76-6.86a1 1 0 0 0 0-1.72L9.52 4.28A1 1 0 0 0 8 5.14Z" /></svg>
            )}
          </div>
        )}
        {(mediaType === 'video' || (carouselItems && carouselItems[slideIndex]?.type === 'video')) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPostVideosMuted(!postVideosMuted); }}
            onDoubleClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            className={`absolute right-3 z-[3] flex h-10 w-10 items-center justify-center rounded-xl bg-black/55 text-white backdrop-blur-sm transition active:scale-90 ${carouselItems ? 'bottom-[3.75rem]' : 'bottom-6'}`}
            aria-label={isVideoMuted ? 'Unmute video' : 'Mute video'}
          >
            <SpeakerWaveIcon muted={isVideoMuted} className="h-6 w-6" strokeWidth={2.5} />
          </button>
        )}
        {showLikeHeart && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <HeartFilledIcon className="w-24 h-24 text-white/90 drop-shadow-lg animate-like-heart" />
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-3 postcard-footer space-y-2">
        <div className="relative flex flex-nowrap items-center justify-between gap-1 text-white">
          <div className="flex min-w-0 flex-nowrap items-center gap-0.5">
            <button onClick={handleLikeClick} className="flex h-12 shrink-0 items-center gap-1.5 rounded-full px-2 max-[359px]:gap-1 max-[359px]:px-1 transition-colors hover:bg-white/10">
                {isLiked ? <HeartFilledIcon className="h-9 w-9 text-red-500" /> : <HeartIcon className="h-9 w-9" />}
                <span className="text-base font-black">{likedBy.length}</span>
            </button>
            {onReactToPost && (
              <ReactionPicker
                reactions={post.reactions}
                currentUserId={currentUser?.id}
                onReact={(emoji) => onReactToPost(id, emoji)}
                onPromptLogin={onPromptLogin}
              />
            )}
            <button onClick={handleCommentClick} className="flex h-12 shrink-0 items-center gap-1.5 rounded-full px-2 max-[359px]:gap-1 max-[359px]:px-1 transition-colors hover:bg-white/10">
                <ThreeDotsIcon className="h-9 w-9" />
                <span className="text-base font-black">{commentsCount}</span>
            </button>
          </div>
          <div ref={settingsRef} className="relative flex shrink-0 flex-nowrap items-center gap-0.5">
            <button
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white max-[359px]:h-8 max-[359px]:w-8 transition-colors hover:bg-white/10 ${showShareMenu ? 'bg-white/10' : ''}`}
              onClick={handleShareClick}
              aria-label="Share post"
            >
              <PaperAirplaneIcon className="block h-6 w-6 shrink-0 text-white" />
            </button>
            <PopupMenu
              open={showShareMenu}
              onClose={() => setShowShareMenu(false)}
              title="Share post"
              className="absolute bottom-full right-0 mb-3"
              items={[
                { key: 'app', label: 'Send in app', description: 'Message it to a rider or a chat', tone: 'accent', icon: <PaperAirplaneIcon className="h-5 w-5" />, onSelect: handleShareInApp },
                {
                  key: 'outside', label: 'Share outside', description: 'Pick another app to share with', tone: 'sky', onSelect: handleShareOutside,
                  icon: (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  ),
                },
              ]}
            />
            <button
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white max-[359px]:h-8 max-[359px]:w-8 transition-colors hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); setIsSettingsOpen((prev) => !prev); }}
              aria-label="Post settings"
            >
              <GearIcon className="block h-6 w-6 shrink-0 text-white" />
            </button>
            <button
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white max-[359px]:h-8 max-[359px]:w-8 transition-colors hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); currentUser ? onToggleSave(id) : onPromptLogin(); }}
              aria-label="Save post"
            >
              <BookmarkIcon className={`block h-6 w-6 shrink-0 text-white ${isSaved ? 'fill-current' : ''}`} />
            </button>
            <PopupMenu
              open={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              title="Post options"
              className="absolute bottom-full right-0 mb-3"
              items={[
                { key: 'profile', label: 'View profile', description: `See @${user.username}'s page`, icon: <UserIcon className="h-5 w-5" />, onSelect: (e) => { e.stopPropagation(); onViewProfile(user); setIsSettingsOpen(false); } },
                ...(!canDelete ? [{
                  key: 'report', label: 'Report post', description: 'Flag it for review', tone: 'danger' as const, icon: <ExclamationIcon className="h-5 w-5" />,
                  onSelect: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsSettingsOpen(false);
                    if (!currentUser) { onPromptLogin(); return; }
                    onReportContent?.('post', id);
                  },
                }] : []),
                ...(canDelete ? [{
                  key: 'delete', label: 'Delete post', description: 'This can’t be undone', tone: 'danger' as const, icon: <TrashIcon className="h-5 w-5" />,
                  onSelect: (e: React.MouseEvent) => { e.stopPropagation(); handleDeleteClick(); },
                }] : []),
              ]}
            />
          </div>
        </div>

        <div className="flex justify-start">
          <p className="max-w-full text-left text-base leading-relaxed break-words text-white">
              <button onClick={handleProfileClick} className="mr-2 text-[18px] font-black text-white hover:underline focus:outline-none">{user.username}</button>
              {renderWithMentions(caption)}
          </p>
        </div>

        {previewComments.length > 0 && (
          <button
            type="button"
            onClick={() => onOpenComments(post)}
            className="mt-1.5 flex w-full flex-col items-start gap-0.5 text-left"
          >
            {previewComments.map((comment) => (
              <p key={comment.id} className="flex w-full items-start gap-1.5 text-[14px] leading-snug text-gray-200">
                <svg viewBox="0 0 24 24" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" fill="currentColor" aria-label="Pinned comment" role="img"><path d="M14.5 3.5 20.5 9.5l-2.2.7-3.4 3.4.3 3.7-1.5 1.5-3.2-3.2-4.9 4.9-1.1-1.1 4.9-4.9-3.2-3.2L6.7 9.4l3.7.3 3.4-3.4z" /></svg>
                <span className="min-w-0 line-clamp-2">
                  <span className="mr-1.5 font-bold text-white">{comment.user.username}</span>
                  {comment.text}
                  {(comment.likedBy?.length || 0) > 0 && <span className="ml-2 text-xs font-semibold text-gray-500">♥ {comment.likedBy!.length}</span>}
                </span>
              </p>
            ))}
            {commentsCount > previewComments.length && (
              <span className="text-[13px] font-semibold text-gray-500">
                View all {commentsCount} comments
              </span>
            )}
          </button>
        )}
      </div>
 
    </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if post data, user state, or essential props change
  return (
    prevProps.post.id === nextProps.post.id &&
    prevProps.post.likedBy.length === nextProps.post.likedBy.length &&
    prevProps.post.savedBy?.length === nextProps.post.savedBy?.length &&
    prevProps.currentUser?.id === nextProps.currentUser?.id &&
    prevProps.allUsers.length === nextProps.allUsers.length &&
    prevProps.post.locationTag?.spotId === nextProps.post.locationTag?.spotId &&
    JSON.stringify(prevProps.post.reactions || {}) === JSON.stringify(nextProps.post.reactions || {})
  );
});

export default PostCard;
